export type LessonRunSyncEventType =
  | 'control_changed'
  | 'step_changed'
  | 'submissions_changed'
  | 'broadcast_changed'
  | 'adaptive_changed';

interface LessonRunSyncPayload {
  runId: number;
  type: LessonRunSyncEventType;
  at: number;
  sourceId: string;
}

type LessonRunSyncHandler = (event: LessonRunSyncPayload) => void;

const CHANNEL_NAME = 'v2-lesson-run-sync';
const STORAGE_KEY = 'v2.lessonRunSync.event';
const SOURCE_ID = `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const canUseWindow = typeof window !== 'undefined';
const canUseBroadcastChannel = canUseWindow && typeof window.BroadcastChannel !== 'undefined';

let sharedChannel: BroadcastChannel | null = null;

const getSharedChannel = (): BroadcastChannel | null => {
  if (!canUseBroadcastChannel) return null;
  if (!sharedChannel) {
    sharedChannel = new window.BroadcastChannel(CHANNEL_NAME);
  }
  return sharedChannel;
};

const normalizePayload = (raw: any): LessonRunSyncPayload | null => {
  if (!raw || typeof raw !== 'object') return null;
  const runId = Number(raw.runId);
  const type = String(raw.type || '').trim() as LessonRunSyncEventType;
  const at = Number(raw.at);
  const sourceId = String(raw.sourceId || '').trim();
  if (!Number.isInteger(runId) || runId <= 0) return null;
  if (!sourceId) return null;
  if (!Number.isFinite(at) || at <= 0) return null;
  if (
    type !== 'control_changed'
    && type !== 'step_changed'
    && type !== 'submissions_changed'
    && type !== 'broadcast_changed'
    && type !== 'adaptive_changed'
  ) {
    return null;
  }
  return { runId, type, at, sourceId };
};

export const publishLessonRunSync = (runId: number, type: LessonRunSyncEventType): void => {
  if (!canUseWindow) return;
  const payload: LessonRunSyncPayload = {
    runId,
    type,
    at: Date.now(),
    sourceId: SOURCE_ID,
  };
  const channel = getSharedChannel();
  if (channel) {
    channel.postMessage(payload);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Remove the key to avoid retaining stale payload in storage.
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage write failures (private mode / quota / policy).
  }
};

export const subscribeLessonRunSync = (
  runId: number,
  handler: LessonRunSyncHandler,
): (() => void) => {
  if (!canUseWindow || !Number.isInteger(runId) || runId <= 0) {
    return () => {};
  }

  const notify = (raw: any) => {
    const payload = normalizePayload(raw);
    if (!payload) return;
    if (payload.runId !== runId) return;
    if (payload.sourceId === SOURCE_ID) return;
    handler(payload);
  };

  const channel = getSharedChannel();
  const onChannelMessage = (event: MessageEvent) => {
    notify(event.data);
  };
  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      notify(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed storage payloads.
    }
  };

  if (channel) {
    channel.addEventListener('message', onChannelMessage);
  }
  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) {
      channel.removeEventListener('message', onChannelMessage);
    }
    window.removeEventListener('storage', onStorage);
  };
};

